/**
 * CapabilitiesPanel — left column.
 *
 * TRUTH RULE (non-negotiable): This panel MUST reflect the live runtime
 * capability manifest served by GET /api/capabilities. Never present a
 * capability as ACTIVE unless it is actually enabled. Never hide a
 * disabled capability behind an "active" label.
 */

import { useEffect, useState } from "react";
import {
    Mic,
    MessageSquare,
    FileText,
    Image as ImageIcon,
    Paperclip,
    Brain,
    Globe,
    Wrench,
    Github,
    Server,
    Network,
    HelpCircle,
} from "lucide-react";
import { PanelShell } from "./PanelShell";
import { fetchCapabilities } from "@/lib/api";

const ICON_BY_ID = {
    voice_conversation: Mic,
    text_conversation: MessageSquare,
    live_transcript: FileText,
    image_input: ImageIcon,
    file_input: Paperclip,
    persistent_cross_session_memory: Brain,
    web_access: Globe,
    external_tools: Wrench,
    emergent_mcp: Network,
    github: Github,
    local_node: Server,
};

const SHORT_LABEL = {
    voice_conversation: "Voice",
    text_conversation: "Text",
    live_transcript: "Transcript",
    image_input: "Vision",
    file_input: "Files",
    persistent_cross_session_memory: "Memory",
    web_access: "Web",
    external_tools: "Tools",
    emergent_mcp: "MCP",
    github: "GitHub",
    local_node: "Local Node",
};

function CapRow({ cap, showAll }) {
    if (!showAll && !cap.enabled) return null;
    const Icon = ICON_BY_ID[cap.id] ?? HelpCircle;
    const label = SHORT_LABEL[cap.id] ?? cap.label;
    return (
        <div
            data-testid={`capability-row-${cap.id}`}
            data-enabled={cap.enabled}
            className="flex items-center justify-between gap-2 py-1.5"
        >
            <div className="flex items-center gap-2 min-w-0">
                <span
                    className={`flex h-6 w-6 items-center justify-center rounded ${
                        cap.enabled
                            ? "bg-qoryn-cyan/10 text-qoryn-cyan"
                            : "bg-white/[0.03] text-qoryn-textMuted"
                    }`}
                >
                    <Icon className="h-3 w-3" strokeWidth={1.75} />
                </span>
                <span
                    className={`font-sans text-[12px] truncate ${
                        cap.enabled ? "text-qoryn-text" : "text-qoryn-textDim"
                    }`}
                    title={cap.label}
                >
                    {label}
                </span>
            </div>
            <span
                data-testid={`capability-state-${cap.id}`}
                className={`font-mono-ui text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${
                    cap.enabled
                        ? "bg-qoryn-mint/10 text-qoryn-mint"
                        : "bg-white/[0.03] text-qoryn-textMuted"
                }`}
            >
                {cap.enabled ? "Active" : "Disabled"}
            </span>
        </div>
    );
}

export function CapabilitiesPanel() {
    const [manifest, setManifest] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchCapabilities()
            .then((m) => !cancelled && setManifest(m))
            .catch((e) => !cancelled && setError(String(e)));
        return () => {
            cancelled = true;
        };
    }, []);

    const caps = manifest?.capabilities ?? [];
    const enabled = caps.filter((c) => c.enabled);
    const disabled = caps.filter((c) => !c.enabled);

    return (
        <PanelShell
            title="Capabilities"
            testId="panel-capabilities"
            right={
                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    {enabled.length}/{caps.length}
                </span>
            }
        >
            {error && (
                <div className="text-red-400 text-xs" data-testid="capabilities-error">
                    Unable to load manifest
                </div>
            )}
            {!manifest && !error && (
                <div className="text-qoryn-textMuted text-xs">Loading…</div>
            )}
            {manifest && (
                <>
                    <div className="divide-y divide-white/[0.04]">
                        {(showAll ? caps : enabled).map((c) => (
                            <CapRow key={c.id} cap={c} showAll={showAll} />
                        ))}
                    </div>
                    {disabled.length > 0 && (
                        <button
                            data-testid="capabilities-toggle-all-btn"
                            onClick={() => setShowAll((v) => !v)}
                            className="mt-2 flex items-center gap-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-cyan transition-colors duration-150 qoryn-focus"
                        >
                            {showAll
                                ? "Hide disabled →"
                                : `View all ${caps.length} modules →`}
                        </button>
                    )}
                </>
            )}
        </PanelShell>
    );
}

export default CapabilitiesPanel;
